import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useOrderPrescriptions from "@hooks/admin/useOrderPrescriptions";
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { Button } from "@runwayhealth/runway-components-react";
import { replaceOrder } from "@store/admin/adminAction";
import { Order } from "@types-runway/case";
import { RootState } from "@types-runway/state";
import { toastWarn } from "@utils/utils";

type OrderManagerProps = {
  order: Order;
  closeModal: () => void;
};

type Prescription = {
  id: string;
  name?: string;
  title?: string;
  product: {
    quantity: number;
    days_supply: number;
  };
};

const OrderManager = ({ order, closeModal }: OrderManagerProps) => {
  const dispatch = useDispatch();
  const [prescriptions, setPrescriptions] = useState([]);
  const { getOrderPrescriptions } = useOrderPrescriptions();
  const orderActionsInFlight = useSelector<RootState, Record<string, boolean>>(
    (state) => state.admin.orderActionsInFlight
  );
  const isReplacing = Boolean(orderActionsInFlight[order.id.toString()]);
  // Only close on the true->false transition (the mutation actually settled),
  // not on initial mount just because the flag happens to be false then.
  const wasReplacing = useRef(isReplacing);
  useEffect(() => {
    if (wasReplacing.current && !isReplacing) {
      closeModal();
    }
    wasReplacing.current = isReplacing;
  }, [isReplacing, closeModal]);

  useEffect(() => {
    // Get Order Prescriptions.
    (async () => {
      const prescriptions = await getOrderPrescriptions(order.id);
      setPrescriptions(prescriptions);
    })();
  }, []);

  const removePrescription = (prescriptionId: string) => {
    setPrescriptions((prevState) =>
      prevState.filter((item: Prescription) => item.id !== prescriptionId)
    );
  };

  const replaceAlert = () => {
    if (isReplacing) {
      toastWarn(
        "Action already in progress",
        `OID ${order.order_id} already has a shipping action in flight. Please wait for it to finish.`
      );
      return;
    }
    if (
      confirm(
        `Are you sure you want to replace order ${order.order_id}?

        This will:
          - Cancel the current order if it hasn't shipped (a shipped order is kept and reshipped).
          - Keep the current order in Runway as history.
          - Generate a new OID funded by the original invoice — the customer is not charged again.
          - Send the new order to the pharmacy.
        `
      )
    ) {
      // The reissue endpoint resolves everything from the order itself —
      // only the id and the admin's prescription selection travel. The modal
      // closes when isReplacing transitions true -> false (see effect above),
      // not immediately — the toast remains the durable outcome signal.
      dispatch(
        replaceOrder({
          orderId: order.id,
          referenceId: order.order_id,
          prescriptions,
        })
      );
    }
  };

  return (
    <>
      <h1>OID: {order.order_id}</h1>
      <h2>Case: {order.caseId}</h2>
      <hr></hr>
      <h3>Shipping Address</h3>
      <hr></hr>
      <p>
        <b>Street: </b>
        {order.Case?.shippingAddress?.street}
      </p>
      <p>
        <b>Secondary:</b> {order.Case?.shippingAddress?.secondary}
      </p>
      <p>
        <b>City: </b>
        {order.Case?.shippingAddress?.city}
      </p>
      <p>
        <b>State: </b>
        {order.Case?.shippingAddress?.state}
      </p>
      <p>
        <b>Postal Code: </b>
        {order.Case?.shippingAddress?.postalCode}
      </p>
      <p>
        <b>Country:</b> {order.Case?.shippingAddress?.country}
      </p>
      <hr></hr>
      <h2>Prescriptions</h2>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <b>ID</b>
            </TableCell>
            <TableCell>
              <b>Name</b>
            </TableCell>
            <TableCell>
              <b>Quantity</b>
            </TableCell>
            <TableCell>
              <b>Supply Days</b>
            </TableCell>
            <TableCell>
              Action: <b>Remove</b>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {prescriptions.map((prescription: Prescription) => (
            <TableRow>
              <TableCell>{prescription.id}</TableCell>
              <TableCell>{prescription.name}</TableCell>
              <TableCell>{prescription.product.quantity}</TableCell>
              <TableCell>{prescription.product.days_supply}</TableCell>
              <TableCell align="center">
                <Button
                  size="sm"
                  iconName="X"
                  isIconOnly
                  variant="outline"
                  onClick={() => removePrescription(prescription.id)}
                ></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p style={{ fontSize: "13px" }}>
        *Prescription changes will not reflect in this table after the replacement, since
        prescriptions source is the consultation and not the order.
      </p>
      <div style={{ padding: "15px" }}>
        <Button iconName="ArrowRight" disabled={isReplacing} onClick={() => replaceAlert()}>
          {isReplacing ? <CircularProgress size={16} /> : "Replace Order"}
        </Button>
      </div>
    </>
  );
};

export default OrderManager;
